<template>
  <div class="content-wrapper">
    <!-- Page Title -->
    <app-page-top-section :title="$t('expenses')">
      <expense-action-button @open-modal="openModal" />
    </app-page-top-section>

    <!-- Search & Filter Row -->
    <div class="mb-3 d-flex flex-wrap gap-2 align-items-center">
      <input
        type="text"
        v-model="search"
        @input="debouncedFetch"
        placeholder="Search..."
        class="form-control w-auto"
      />

      <!-- Filter Type -->
      <select
        v-model="filterType"
        class="form-control w-auto"
        @change="onFilterTypeChange"
      >
        <option value="">All</option>
        <option value="monthly">Monthly</option>
        <option value="yearly">Yearly</option>
        <option value="current_month_ts">Current Month</option>
      </select>

      <!-- Filter Value Inputs -->
      <input
        v-if="filterType === 'monthly'"
        type="month"
        v-model="filterValue"
        class="form-control w-auto"
        @change="fetchExpenses"
      />
      <input
        v-if="filterType === 'yearly'"
        type="number"
        min="2000"
        max="2100"
        v-model="filterValue"
        placeholder="Year"
        class="form-control w-auto"
        @change="fetchExpenses"
      />

      <!-- Category Filter -->
      <select
        v-model="categoryId"
        class="form-control w-auto"
        @change="fetchExpenses"
      >
        <option value="">All Categories</option>
        <option v-for="cat in categories" :key="cat.id" :value="cat.id">
          {{ cat.name }}
        </option>
      </select>
    </div>

    <!-- Expenses Table -->
    <table class="table table-bordered">
      <thead>
        <tr>
          <th>{{ $t('id') }}</th>
          <th>{{ $t('title') }}</th>
          <th>{{ $t('category') }}</th>
          <th>{{ $t('amount') }}</th>
          <th>{{ $t('description') }}</th>
          <th>{{ $t('user_name') }}</th>
          <th>{{ $t('month_year') }}</th>
          <th>{{ $t('file') }}</th>
          <th>{{ $t('actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(expense, index) in expenses.data" :key="expense.id">
          <td>{{ (expenses.current_page - 1) * 10 + index + 1 }}</td>
          <td>{{ expense.title || '-' }}</td>
          <td>{{ expense.category?.name || '-' }}</td>
          <td>{{ expense.amount }}</td>
          <td>{{ expense.description || '-' }}</td>
          <td>{{ expense.user ? expense.user.full_name : '-' }}</td>
          <td>{{ expense.month_year_formatted || '-' }}</td>
          <td>
            <!-- <a
              v-if="expense.file"
              :href="'/' + expense.file"
              target="_blank"
              class="btn btn-sm btn-info"
            >
              View File
            </a>
            <span v-else>-</span> -->

              <div v-if="expense.file">
      
               <a :href="`/expenses/${expense.id}/download`"  class="btn btn-sm btn-info">Download</a>
            </div>
            <div v-else>-</div>
          </td>
          <td>
            <a :href="`/expenses/${expense.id}/invoice`" target="_blank" class="btn btn-sm btn-success">Invoice</a>
            <button class="btn btn-sm btn-primary" @click="editExpense(expense)">Edit</button>
            <button class="btn btn-sm btn-danger" @click="deleteExpense(expense.id)">Delete</button>
          </td>
        </tr>
        <tr v-if="!expenses.data.length">
          <td colspan="9" class="text-center text-muted">No expenses found</td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <nav v-if="expenses.last_page > 1" aria-label="Page navigation">
      <ul class="pagination custom-pagination">
        <li
          class="page-item"
          :class="{ disabled: expenses.current_page === 1 }"
          @click="changePage(expenses.current_page - 1)"
        >
          <a class="page-link" href="#" aria-label="Previous" @click.prevent>&laquo;</a>
        </li>
        <li
          v-for="page in totalPages"
          :key="page"
          class="page-item"
          :class="{ active: page === expenses.current_page }"
          @click="changePage(page)"
        >
          <a class="page-link" href="#" @click.prevent>{{ page }}</a>
        </li>
        <li
          class="page-item"
          :class="{ disabled: expenses.current_page === expenses.last_page }"
          @click="changePage(expenses.current_page + 1)"
        >
          <a class="page-link" href="#" aria-label="Next" @click.prevent>&raquo;</a>
        </li>
      </ul>
    </nav>

    <!-- Expense Modal -->
    <expense-form-modal
      v-if="isModalActive"
      v-model="isModalActive"
      :expense="selectedExpense"
      @saved="fetchExpenses"
    />
  </div>
</template>

<script>
import axios from 'axios'
import ExpenseActionButton from './Components/ExpenseActionButton.vue'
import ExpenseFormModal from './Components/ExpenseFormModal.vue'
import _ from 'lodash'

export default {
  name: 'AppExpenses',
  components: { ExpenseActionButton, ExpenseFormModal },
  data() {
    return {
      expenses: { data: [], current_page: 1, last_page: 1, total: 0 },
      categories: [],
      categoryId: '',       // selected category filter
      isModalActive: false,
      selectedExpense: null,
      search: '',
      filterType: '',
      filterValue: '',
    }
  },
  created() {
    this.debouncedFetch = _.debounce(this.fetchExpenses, 500)
    this.fetchCategories()
    this.fetchExpenses()
  },
  computed: {
    totalPages() {
      const total = this.expenses.last_page
      const current = this.expenses.current_page
      const delta = 3
      let range = []
      let start = Math.max(1, current - delta)
      let end = Math.min(total, current + delta)
      for (let i = start; i <= end; i++) range.push(i)
      return range
    }
  },
  methods: {
    fetchCategories() {
      axios.get('/expenses/categories')
        .then(res => { this.categories = res.data })
        .catch(err => console.error('Fetch categories error:', err))
    },
    fetchExpenses(page = 1) {
      this.expenses.current_page = page
      axios.get('/expenses', {
        params: {
          page: page,
          search: this.search,
          filter_type: this.filterType,
          filter_value: this.filterType === 'monthly' || this.filterType === 'yearly' ? this.filterValue : '',
          category_id: this.categoryId,
        },
      })
      .then(res => { this.expenses = res.data })
      .catch(err => console.error('Fetch expenses error:', err))
    },
    changePage(page) {
      if (page < 1 || page > this.expenses.last_page) return
      this.fetchExpenses(page)
    },
    onFilterTypeChange() {
      this.filterValue = ''
      this.fetchExpenses()
    },
    openModal() {
      this.selectedExpense = null
      this.isModalActive = true
    },
    editExpense(expense) {
      this.selectedExpense = expense
      this.isModalActive = true
    },
    deleteExpense(id) {
      if (confirm('Are you sure?')) {
        axios.delete(`/expenses/${id}`)
          .then(() => {
            this.fetchExpenses(this.expenses.current_page)
            alert('Deleted successfully!')
          })
      }
    },
  },
}
</script>

<style scoped>
.custom-pagination {
  display: flex;
  list-style: none;
  padding-left: 0;
  user-select: none;
}
.custom-pagination .page-item {
  margin: 0 3px;
}
.custom-pagination .page-item.disabled .page-link {
  color: #ccc;
  cursor: not-allowed;
  pointer-events: none;
}
.custom-pagination .page-item .page-link {
  display: block;
  padding: 6px 12px;
  color: #007bff;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  text-decoration: none;
}
.custom-pagination .page-item .page-link:hover:not(.disabled) {
  background-color: #e9ecef;
  text-decoration: none;
}
.custom-pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
  cursor: default;
}
</style>
